import React, { useCallback, useEffect, useState } from 'react';
import { Play, RefreshCw, Loader2, Radio } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { eventBusAPI } from '../services/api';
import { getErrorMessage } from '../lib/utils';

const STATUS_STYLES = {
  pending: 'bg-amber-500/15 text-amber-600',
  processing: 'bg-blue-500/15 text-blue-600',
  delivered: 'bg-green-500/15 text-green-600',
  processed: 'bg-green-500/15 text-green-600',
  failed: 'bg-red-500/15 text-red-600',
};

const badge = (status) => STATUS_STYLES[String(status || '').toLowerCase()] || 'bg-muted text-muted-foreground';
const fmt = (value) => (value ? new Date(value).toLocaleString() : '—');

const EventBusModule = () => {
  const [events, setEvents] = useState([]);
  const [subs, setSubs] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [failedOnly, setFailedOnly] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [eventsRes, subsRes, delRes] = await Promise.allSettled([
        eventBusAPI.listDomainEvents(50),
        eventBusAPI.listSubscriptions(),
        eventBusAPI.listDeliveries(50),
      ]);
      if (eventsRes.status === 'fulfilled') setEvents(eventsRes.value?.data || []);
      if (subsRes.status === 'fulfilled') setSubs(subsRes.value?.data || []);
      if (delRes.status === 'fulfilled') setDeliveries(delRes.value?.data || []);
      const failure = [eventsRes, subsRes, delRes].find((r) => r.status === 'rejected');
      if (failure) toast.error(getErrorMessage(failure.reason, 'Failed to load the event bus'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runQueue = async () => {
    try {
      setProcessing(true);
      const res = await eventBusAPI.processQueue(50);
      const summary = res?.data || {};
      toast.success(
        `Queue processed${summary.processed != null ? ` — ${summary.processed} event(s)` : ''}`
      );
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not process the queue'));
    } finally {
      setProcessing(false);
    }
  };

  const shownDeliveries = failedOnly
    ? deliveries.filter((d) => String(d.status || '').toLowerCase() === 'failed')
    : deliveries;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
            <Radio className="h-5 w-5" /> Event Bus
          </h2>
          <p className="text-sm text-muted-foreground">
            Domain events, who consumes them, and every delivery attempt.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button onClick={runQueue} disabled={processing}>
            {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Play className="h-4 w-4 mr-1" /> Process queue</>}
          </Button>
        </div>
      </div>

      <Card className="bg-card border-border">
        <CardHeader className="border-b border-border/50 bg-muted/20">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-foreground text-base">Deliveries</CardTitle>
            <Button size="sm" variant={failedOnly ? 'default' : 'outline'} onClick={() => setFailedOnly((v) => !v)}>
              Failed only
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : shownDeliveries.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">
              {failedOnly ? 'No failed deliveries.' : 'No deliveries recorded.'}
            </p>
          ) : (
            <div className="space-y-2">
              {shownDeliveries.map((d) => (
                <div key={d.id} className="flex items-start justify-between gap-3 rounded-lg border border-border p-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge className={badge(d.status)}>{d.status}</Badge>
                      <p className="text-sm text-foreground">
                        event #{d.domain_event_id} → subscription #{d.subscription_id}
                      </p>
                    </div>
                    {d.error_message && (
                      <p className="text-xs text-red-500 mt-0.5 break-all">{d.error_message}</p>
                    )}
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      Attempt {d.attempt_number} · created {fmt(d.created_at)} · processed {fmt(d.processed_at)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-card border-border">
        <CardHeader className="border-b border-border/50 bg-muted/20">
          <CardTitle className="text-foreground text-base">Recent domain events</CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          {events.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">No events published yet.</p>
          ) : (
            <div className="space-y-2">
              {events.map((e) => (
                <div key={e.id} className="flex items-start justify-between gap-3 rounded-lg border border-border p-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge className={badge(e.status)}>{e.status}</Badge>
                      <p className="text-sm font-medium text-foreground truncate">{e.event_key}</p>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {e.aggregate_type}#{e.aggregate_id} · from {e.source_service}
                    </p>
                    {e.last_error && <p className="text-xs text-red-500 mt-0.5 break-all">{e.last_error}</p>}
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      Occurred {fmt(e.occurred_at)} · processed {fmt(e.processed_at)}
                      {e.retry_count ? ` · ${e.retry_count} retry(s)` : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-card border-border">
        <CardHeader className="border-b border-border/50 bg-muted/20">
          <CardTitle className="text-foreground text-base">Subscriptions</CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          {subs.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">No subscriptions registered.</p>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2">
              {subs.map((s) => (
                <div key={s.id} className="rounded-lg border border-border p-3">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-foreground truncate">{s.subscription_key}</p>
                    {s.is_active
                      ? <Badge className="bg-green-500/15 text-green-600">active</Badge>
                      : <Badge className="bg-muted text-muted-foreground">off</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {s.event_key} → {s.consumer_service}.{s.handler_name}
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">via {s.transport}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EventBusModule;
